import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../../config/firebase';
import {
  Award,
  TrendingUp,
  Star,
  Users,
  Calendar,
  Plus,
  Edit2,
  Eye,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  XCircle,
  CheckCircle,
  BarChart3,
  Target
} from 'lucide-react';
import './PerformanceTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const PerformanceTab = ({ hotelId, token, user }) => {
  const resolvedToken = (
    token ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token')
  );

  const sanitizedToken = typeof resolvedToken === 'string'
    ? resolvedToken.replace(/^"+|"+$/g, '').trim()
    : '';

  const getAuthConfig = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken(false);
        if (idToken) {
          return { headers: { Authorization: `Bearer ${idToken}` } };
        }
      }
    } catch (_) {
    }

    if (sanitizedToken) {
      return {
        headers: {
          Authorization: sanitizedToken.startsWith('Bearer ')
            ? sanitizedToken
            : `Bearer ${sanitizedToken}`
        }
      };
    }

    return {};
  };

  // Data state
  const [reviews, setReviews] = useState([]);
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('list'); // list, create, view
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  
  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingReview, setDeletingReview] = useState(null);
  
  // Form state - Performance Review
  const [reviewFormData, setReviewFormData] = useState({
    staff_id: '',
    review_period_start: '',
    review_period_end: '',
    overall_rating: 5,
    strengths: '',
    areas_for_improvement: '',
    goals: '',
    comments: '',
    criteria: [
      { name: 'Job Knowledge', rating: 5, weight: 20 },
      { name: 'Quality of Work', rating: 5, weight: 20 },
      { name: 'Communication', rating: 5, weight: 15 },
      { name: 'Teamwork', rating: 5, weight: 15 },
      { name: 'Attendance & Punctuality', rating: 5, weight: 15 },
      { name: 'Initiative', rating: 5, weight: 15 }
    ]
  });

  // Fetch initial data
  useEffect(() => {
    if (hotelId) {
      fetchReviews();
      fetchStaff();
      fetchDepartments();
    }
  }, [hotelId, token]);

  // API Calls
  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) return;

      const response = await axios.get(`${API_BASE_URL}/performance/reviews/hotel/${hotelId}`, authConfig);
      setReviews(response.data?.data || response.data || []);
    } catch (err) {
      setError('Failed to load performance reviews');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) return;

      const response = await axios.get(`${API_BASE_URL}/manager/staff`, authConfig);
      const rawStaff = response.data?.data?.staff || response.data?.data || response.data || [];
      setStaff(rawStaff.map((member) => ({
        id: member.staff_profile_id ?? member.profile_id ?? member.user_id ?? member.id ?? null,
        name: member.full_name || member.name || member.staff_name,
        department_name: member.department_name || 'N/A',
        staff_code: member.staff_id || member.employee_number || null
      })).filter((member) => member.id !== null && member.id !== undefined && member.id !== ''));
    } catch (err) {
      console.error('Failed to fetch staff:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) return;

      const response = await axios.get(`${API_BASE_URL}/departments`, authConfig);
      const rows = response.data?.data || response.data || [];
      setDepartments(rows.map((dept) => ({
        id: dept.department_id ?? dept.id,
        name: dept.department_name ?? dept.name
      })));
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchReviewDetails = async (reviewId) => {
    setLoading(true);
    setError(null);
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) return;

      const response = await axios.get(`${API_BASE_URL}/performance/reviews/${reviewId}`, authConfig);
      const payload = response.data?.data || response.data || {};
      const review = payload.review || payload;
      setSelectedReview({
        id: review.review_id ?? review.id,
        staff_name: review.staff_name,
        department_name: review.department_name,
        review_period_start: review.review_period_start,
        review_period_end: review.review_period_end,
        overall_rating: review.overall_rating,
        strengths: review.strengths,
        areas_for_improvement: review.areas_for_improvement,
        goals: review.goals_for_next_period,
        comments: review.reviewer_comments,
        reviewer_name: review.reviewer_name,
        created_at: review.created_at,
        criteria: JSON.stringify((payload.scores || []).map((score) => ({
          name: score.criteria_name,
          rating: score.max_score ? (parseFloat(score.score || 0) / parseFloat(score.max_score || 1)) * 5 : parseFloat(score.score || 0),
          weight: parseFloat(score.weight_percentage || 0)
        })))
      });
      setActiveView('view');
    } catch (err) {
      setError('Failed to load review details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const calculatedRating = calculateWeightedRating();
      
      await axios.post(
        `${API_BASE_URL}/performance/reviews`,
        {
          staff_profile_id: reviewFormData.staff_id,
          hotel_id: hotelId,
          review_period_start: reviewFormData.review_period_start,
          review_period_end: reviewFormData.review_period_end,
          overall_rating: calculatedRating,
          strengths: reviewFormData.strengths,
          areas_for_improvement: reviewFormData.areas_for_improvement,
          goals_for_next_period: reviewFormData.goals,
          comments: reviewFormData.comments,
          status: 'completed'
        },
        await getAuthConfig()
      );

      resetForm();
      setActiveView('list');
      fetchReviews();
    } catch (err) {
      setError('Failed to create review');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!deletingReview) return;

    setLoading(true);
    setError(null);

    try {
      await axios.delete(`${API_BASE_URL}/performance/reviews/${deletingReview.id}`, await getAuthConfig());

      setShowDeleteModal(false);
      setDeletingReview(null);
      fetchReviews();
      
      if (selectedReview?.id === deletingReview.id) {
        setSelectedReview(null);
        setActiveView('list');
      }
    } catch (err) {
      setError('Failed to delete review');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const resetForm = () => {
    setReviewFormData({
      staff_id: '',
      review_period_start: '',
      review_period_end: '',
      overall_rating: 5,
      strengths: '',
      areas_for_improvement: '',
      goals: '',
      comments: '',
      criteria: [
        { name: 'Job Knowledge', rating: 5, weight: 20 },
        { name: 'Quality of Work', rating: 5, weight: 20 },
        { name: 'Communication', rating: 5, weight: 15 },
        { name: 'Teamwork', rating: 5, weight: 15 },
        { name: 'Attendance & Punctuality', rating: 5, weight: 15 },
        { name: 'Initiative', rating: 5, weight: 15 }
      ]
    });
  };

  const calculateWeightedRating = () => {
    const totalWeight = reviewFormData.criteria.reduce((sum, c) => sum + c.weight, 0);
    const weightedSum = reviewFormData.criteria.reduce((sum, c) => sum + (c.rating * c.weight), 0);
    return (weightedSum / totalWeight).toFixed(2);
  };

  const updateCriteriaRating = (index, rating) => {
    const newCriteria = [...reviewFormData.criteria];
    newCriteria[index].rating = rating;
    setReviewFormData({ ...reviewFormData, criteria: newCriteria });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const normalizeRating = (rating) => {
    if (typeof rating === 'number') return rating;

    const numeric = parseFloat(rating);
    if (!Number.isNaN(numeric)) return numeric;

    const enumMap = {
      outstanding: 5.0,
      exceeds_expectations: 4.0,
      meets_expectations: 3.0,
      needs_improvement: 2.0,
      unsatisfactory: 1.0
    };

    return enumMap[rating] || 0;
  };

  const getRatingColor = (rating) => {
    const value = normalizeRating(rating);
    if (value >= 4.5) return 'excellent';
    if (value >= 3.5) return 'good';
    if (value >= 2.5) return 'average';
    return 'poor';
  };

  const getRatingLabel = (rating) => {
    const value = normalizeRating(rating);
    if (value >= 4.5) return 'Excellent';
    if (value >= 3.5) return 'Good';
    if (value >= 2.5) return 'Average';
    if (value >= 1.5) return 'Below Average';
    return 'Poor';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { icon: Edit2, color: 'draft', label: 'Draft' },
      completed: { icon: CheckCircle, color: 'completed', label: 'Completed' },
      pending: { icon: AlertCircle, color: 'pending', label: 'Pending' }
    };

    const config = statusMap[status] || statusMap.draft;
    const Icon = config.icon;

    return (
      <span className={`status-badge ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const openDeleteModal = (review) => {
    setDeletingReview(review);
    setShowDeleteModal(true);
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.staff_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || review.status === filterStatus;
    const matchesDept = filterDepartment === 'all' || review.department_id === parseInt(filterDepartment);
    return matchesSearch && matchesStatus && matchesDept;
  });

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + normalizeRating(review.overall_rating), 0);
    return (sum / reviews.length).toFixed(2);
  };

  const getReviewsThisMonth = () => {
    const now = new Date();
    return reviews.filter(review => {
      const reviewDate = new Date(review.created_at);
      return reviewDate.getMonth() === now.getMonth() && 
             reviewDate.getFullYear() === now.getFullYear();
    }).length;
  };

  return (
    <div className="performance-tab">
      {/* Header */}
      <div className="performance-header">
        <div className="header-left">
          <h2>
            <Award size={24} />
            Performance Reviews
          </h2>
          <p>Conduct performance evaluations and track staff development</p>
        </div>
        <div className="header-actions">
          {activeView !== 'list' && (
            <button className="btn-secondary" onClick={() => {
              setActiveView('list');
              setSelectedReview(null);
              resetForm();
            }}>
              ← Back to Reviews
            </button>
          )}
          <button className="btn-icon" onClick={fetchReviews}>
            <RefreshCw size={18} />
          </button>
          {activeView === 'list' && (
            <button className="btn-primary" onClick={() => {
              resetForm();
              setActiveView('create');
            }}>
              <Plus size={18} />
              New Review
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><XCircle size={16} /></button>
        </div>
      )}

      {/* List View */}
      {activeView === 'list' && (
        <>
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon total">
                <Award size={24} />
              </div>
              <div className="card-content">
                <h3>{reviews.length}</h3>
                <p>Total Reviews</p>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon average">
                <Star size={24} />
              </div>
              <div className="card-content">
                <h3>{calculateAverageRating()}/5.0</h3>
                <p>Average Rating</p>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon monthly">
                <TrendingUp size={24} />
              </div>
              <div className="card-content">
                <h3>{getReviewsThisMonth()}</h3>
                <p>This Month</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
            </select>
            <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          {/* Reviews List */}
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading reviews...</p>
            </div>
          ) : (
            <div className="reviews-grid">
              {filteredReviews.map(review => (
                <div key={review.id} className="review-card">
                  <div className="card-header">
                    <div className="staff-info">
                      <div className="staff-avatar">
                        <Users size={20} />
                      </div>
                      <div>
                        <h4>{review.staff_name}</h4>
                        <p>{review.department_name}</p>
                      </div>
                    </div>
                    {getStatusBadge(review.status)}
                  </div>

                  <div className="review-rating">
                    <div className={`rating-display ${getRatingColor(review.overall_rating)}`}>
                      <Star size={32} fill="currentColor" />
                      <span className="rating-number">{normalizeRating(review.overall_rating).toFixed(1)}</span>
                    </div>
                    <span className="rating-label">{getRatingLabel(review.overall_rating)}</span>
                  </div>

                  <div className="review-meta">
                    <div className="meta-item">
                      <Calendar size={14} />
                      <span>Period: {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}</span>
                    </div>
                    <div className="meta-item">
                      <Users size={14} />
                      <span>Reviewer: {review.reviewer_name}</span>
                    </div>
                  </div>

                  <div className="card-actions">
                    <button onClick={() => fetchReviewDetails(review.id)} className="btn-view">
                      <Eye size={16} />
                      View Details
                    </button>
                    <button onClick={() => openDeleteModal(review)} className="btn-delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {filteredReviews.length === 0 && (
                <div className="empty-state">
                  <Award size={48} />
                  <h3>No reviews found</h3>
                  <p>Create your first performance review to get started</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Review View */}
      {activeView === 'create' && (
        <div className="create-review">
          <div className="form-header">
            <h3>New Performance Review</h3>
            <p>Complete all sections to create a comprehensive performance evaluation</p>
          </div>

          <form onSubmit={handleCreateReview}>
            {/* Basic Information */}
            <div className="form-section">
              <h4>Basic Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Staff Member *</label>
                  <select
                    value={reviewFormData.staff_id}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, staff_id: e.target.value })}
                    required
                  >
                    <option value="">Select staff member</option>
                    {staff.map(member => (
                      <option key={`${member.id}-${member.staff_code || 'staff'}`} value={member.id}>
                        {member.name} - {member.department_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Review Period Start *</label>
                  <input
                    type="date"
                    value={reviewFormData.review_period_start}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, review_period_start: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Review Period End *</label>
                  <input
                    type="date"
                    value={reviewFormData.review_period_end}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, review_period_end: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Performance Criteria */}
            <div className="form-section">
              <h4>Performance Criteria</h4>
              <p className="section-description">Rate each criterion from 1 (Poor) to 5 (Excellent)</p>
              
              <div className="criteria-list">
                {reviewFormData.criteria.map((criterion, index) => (
                  <div key={index} className="criterion-item">
                    <div className="criterion-header">
                      <span className="criterion-name">{criterion.name}</span>
                      <span className="criterion-weight">{criterion.weight}%</span>
                    </div>
                    <div className="rating-slider">
                      <input
                        type="range"
                        min="1"
                        max="5"
                        step="0.5"
                        value={criterion.rating}
                        onChange={(e) => updateCriteriaRating(index, parseFloat(e.target.value))}
                      />
                      <div className="rating-labels">
                        <span>1</span>
                        <span className={`rating-value ${getRatingColor(criterion.rating)}`}>
                          {criterion.rating.toFixed(1)}
                        </span>
                        <span>5</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="calculated-rating">
                <Target size={24} />
                <div>
                  <span className="label">Calculated Overall Rating</span>
                  <span className={`value ${getRatingColor(calculateWeightedRating())}`}>
                    {calculateWeightedRating()} / 5.0
                  </span>
                </div>
              </div>
            </div>

            {/* Detailed Feedback */}
            <div className="form-section">
              <h4>Detailed Feedback</h4>
              <div className="form-group">
                <label>Strengths</label>
                <textarea
                  value={reviewFormData.strengths}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, strengths: e.target.value })}
                  placeholder="List the staff member's key strengths and accomplishments"
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Areas for Improvement</label>
                <textarea
                  value={reviewFormData.areas_for_improvement}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, areas_for_improvement: e.target.value })}
                  placeholder="Identify areas where the staff member can develop further"
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Goals for Next Period</label>
                <textarea
                  value={reviewFormData.goals}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, goals: e.target.value })}
                  placeholder="Set clear, actionable goals for the upcoming period"
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Additional Comments</label>
                <textarea
                  value={reviewFormData.comments}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, comments: e.target.value })}
                  placeholder="Any additional feedback or observations"
                  rows="3"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setActiveView('list')}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating Review...' : 'Create Review'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* View Review Details */}
      {activeView === 'view' && selectedReview && (
        <div className="view-review">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading review details...</p>
            </div>
          ) : (
            <>
              {/* Review Header */}
              <div className="review-header-detail">
                <div className="header-info">
                  <div className="staff-avatar-large">
                    <Users size={32} />
                  </div>
                  <div>
                    <h3>{selectedReview.staff_name}</h3>
                    <p>{selectedReview.department_name}</p>
                    <div className="review-period">
                      <Calendar size={14} />
                      <span>{formatDate(selectedReview.review_period_start)} - {formatDate(selectedReview.review_period_end)}</span>
                    </div>
                  </div>
                </div>
                <div className={`overall-rating ${getRatingColor(selectedReview.overall_rating)}`}>
                  <Star size={48} fill="currentColor" />
                  <span className="rating-number">{normalizeRating(selectedReview.overall_rating).toFixed(1)}</span>
                  <span className="rating-label">{getRatingLabel(selectedReview.overall_rating)}</span>
                </div>
              </div>

              {/* Performance Criteria */}
              {selectedReview.criteria && (
                <div className="detail-section">
                  <h4>Performance Criteria</h4>
                  <div className="criteria-chart">
                    {JSON.parse(selectedReview.criteria).map((criterion, index) => (
                      <div key={index} className="criterion-bar">
                        <div className="criterion-info">
                          <span className="name">{criterion.name}</span>
                          <span className="rating">{criterion.rating.toFixed(1)}/5.0</span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className={`progress-fill ${getRatingColor(criterion.rating)}`}
                            style={{ width: `${(criterion.rating / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Feedback */}
              <div className="detail-section">
                <h4>Strengths</h4>
                <p className="feedback-text">{selectedReview.strengths || 'No strengths noted'}</p>
              </div>

              <div className="detail-section">
                <h4>Areas for Improvement</h4>
                <p className="feedback-text">{selectedReview.areas_for_improvement || 'No areas noted'}</p>
              </div>

              <div className="detail-section">
                <h4>Goals for Next Period</h4>
                <p className="feedback-text">{selectedReview.goals || 'No goals set'}</p>
              </div>

              {selectedReview.comments && (
                <div className="detail-section">
                  <h4>Additional Comments</h4>
                  <p className="feedback-text">{selectedReview.comments}</p>
                </div>
              )}

              {/* Review Metadata */}
              <div className="review-metadata">
                <div className="meta-item">
                  <Users size={16} />
                  <span>Reviewed by: {selectedReview.reviewer_name}</span>
                </div>
                <div className="meta-item">
                  <Calendar size={16} />
                  <span>Created: {formatDate(selectedReview.created_at)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingReview && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Review</h3>
              <button onClick={() => setShowDeleteModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">
                <AlertCircle size={48} />
              </div>
              <p>Are you sure you want to delete the performance review for <strong>{deletingReview.staff_name}</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteReview} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceTab;